import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import PayPalCheckout from "@/components/PayPalCheckout";
import { API } from "@/auth/AuthContext";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/shared";

export function formatPrice(s) {
  switch (s.price_type) {
    case "fixed":
      return `$${s.price}`;
    case "per_unit":
      return `$${s.price} ${s.unit || "each"}`;
    case "starting":
      return `Starting at $${s.price}`;
    case "range":
      return `$${s.price}–$${s.price_max}`;
    default:
      return "Custom Quote";
  }
}

const SEGMENTS = [
  {
    id: "residential",
    label: "Residential",
    title: "Residential Technology Services",
    desc: "Professional technology installation and smart home solutions designed for modern living.",
  },
  {
    id: "commercial",
    label: "Commercial",
    title: "Commercial & Infrastructure",
    desc: "Professional technology infrastructure solutions for businesses, commercial facilities, and enterprise environments.",
  },
  {
    id: "digital",
    label: "Digital",
    title: "Digital Solutions",
    desc: "Modern websites, digital infrastructure, and business automation solutions designed to help companies grow.",
  },
  {
    id: "local-ai",
    label: "Local AI",
    title: "Local AI & Automation",
    desc: "Private AI systems and intelligent automation for homes, businesses, and enterprise environments.",
  },
];

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState("residential");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", contact: "", location: "", preferred_date: "", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paid, setPaid] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then((r) => setServices(r.data))
      .catch(() => toast.error("Could not load services. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const map = new Map();
    for (const s of services.filter((x) => (x.segment || "residential") === segment)) {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category).push(s);
    }
    return [...map.entries()];
  }, [services, segment]);

  const active = SEGMENTS.find((s) => s.id === segment);

  const addItem = (s) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.key === s.key);
      if (existing) {
        if (s.price_type === "custom") return prev;
        return prev.map((i) => (i.key === s.key ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, { key: s.key, name: s.name, qty: 1, unit_price: s.price, price_type: s.price_type, checkout_eligible: !!s.checkout_eligible }];
    });
  };

  const setQty = (key, delta) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(0, i.qty + delta) } : i))
        .filter((i) => i.qty > 0)
    );

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const estimate = items.reduce((sum, i) => (i.unit_price ? sum + i.unit_price * i.qty : sum), 0);
  const hasQuoteOnly = items.some((i) => i.unit_price == null);
  const canCheckout =
    items.length > 0 &&
    items.every((i) => i.checkout_eligible && (i.price_type === "fixed" || i.price_type === "per_unit"));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        service: hasQuoteOnly ? "Custom Quote Request" : "Online Estimate Request",
        items: items.map((i) => ({ key: i.key, name: i.name, qty: i.qty })),
        estimate: estimate || null,
      });
      setSubmitted(true);
      toast.success("Request received — DuroTech28 will reach out shortly.");
    } catch (err) {
      toast.error(`Could not submit right now. Please call ${PHONE_DISPLAY}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Services & Prices | DuroTech28 — Dallas–Fort Worth"
        description="TV mounting, smart home, security cameras, Wi-Fi, structured cabling, solar and digital services across Dallas–Fort Worth. Clear pricing. Call 469-897-6497."
        canonical="https://durotech28.me/services"
        jsonLd={[{
          "@context": "https://schema.org", "@type": "Service",
          "name": "Low Voltage Installation Services",
          "serviceType": "TV mounting, smart home installation, security cameras, Wi-Fi and mesh networking, structured cabling, solar installation support, digital business solutions",
          "provider": { "@type": "LocalBusiness", "name": "DuroTech28", "telephone": "+1-469-897-6497", "url": "https://durotech28.me" },
          "areaServed": "Dallas–Fort Worth Metroplex",
          "url": "https://durotech28.me/services",
        }]}
      />
      <Nav />
      <main className="max-w-6xl mx-auto px-6 py-14" data-testid="services-page">
        <div className="max-w-2xl mb-10">
          <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">One Catalog · One Booking Experience</span>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white mt-2">Explore Services</h1>
          <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
            Pick a category, choose your services, and the right next step appears automatically —
            instant booking for priced services, custom quotes for complex projects.
          </p>
        </div>

        {/* Segment tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-10" data-testid="segment-tabs">
          {SEGMENTS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSegment(s.id)}
              data-testid={`segment-tab-${s.id}`}
              className={`rounded-xl px-3 py-4 text-center transition-colors border ${
                segment === s.id
                  ? "bg-[#d4af37] border-[#d4af37] text-black"
                  : "bg-[#121216] border-zinc-800 text-zinc-300 hover:border-[#d4af37]/50"
              }`}
            >
              <span className="font-display uppercase tracking-wide text-sm sm:text-lg block">{s.label}</span>
              <span className={`text-[9px] font-mono uppercase tracking-widest hidden sm:block mt-1 ${segment === s.id ? "text-black/70" : "text-zinc-500"}`}>
                {s.id === "residential" ? "Homes & Smart Living" : s.id === "commercial" ? "Business & Data Centers" : s.id === "digital" ? "Websites & Automation" : "Intelligent Systems"}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-zinc-500 font-mono text-xs" data-testid="services-loading">Loading catalog...</p>
        ) : (
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            {/* Catalog */}
            <div className="lg:col-span-2">
              <div className="mb-8">
                <h2 className="font-display text-2xl uppercase tracking-tight text-white">{active.title}</h2>
                <p className="text-xs text-zinc-400 mt-1.5">{active.desc}</p>
                {active.id === "local-ai" && (
                  <a href="/local-ai" data-testid="local-ai-learn-more"
                    className="inline-block mt-3 text-[11px] font-mono uppercase tracking-wider text-[#d4af37] hover:underline">
                    Explore Local AI in depth →
                  </a>
                )}
              </div>
              <div className="space-y-12">
                {categories.map(([category, list]) => (
                  <section key={category} data-testid={`category-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex items-center gap-4 mb-5">
                      <h3 className="font-display text-lg uppercase tracking-wide text-[#d4af37] whitespace-nowrap">{category}</h3>
                      <div className="h-px flex-1 gold-line"></div>
                    </div>
                    <div className="space-y-2.5">
                      {list.map((s) => (
                        <div
                          key={s.key}
                          data-testid={`service-row-${s.key}`}
                          className="flex items-center justify-between gap-4 rounded-lg bg-[#121216] border border-zinc-800 hover:border-[#d4af37]/30 transition-colors px-4 py-3"
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-zinc-100 font-medium">{s.name}</p>
                            {s.note && <p className="text-[10px] text-zinc-500 font-mono mt-0.5 leading-relaxed">{s.note}</p>}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-sm font-mono text-right ${s.price_type === "custom" ? "text-zinc-400" : "text-[#d4af37] font-semibold"}`}>
                            {formatPrice(s)}
                          </span>
                          {s.key.startsWith("tv-mount-") && s.key !== "tv-mount-100-plus" ? (
                            <a href="/book" data-testid={`service-book-btn-${s.key}`}
                              className="px-3 py-1.5 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors">
                              Book Now →
                            </a>
                          ) : (
                          <button
                            type="button"
                            onClick={() => addItem(s)}
                            data-testid={`service-add-btn-${s.key}`}
                            className={
                              s.price_type === "custom"
                                ? "px-3 py-1.5 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors"
                                : "px-3 py-1.5 rounded bg-[#d4af37]/10 border border-[#d4af37]/30 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors"
                            }
                          >
                            {s.price_type === "custom" ? "Request Quote" : "Add"}
                          </button>
                          )}
                        </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>

            {/* Estimate panel */}
            <aside className="lg:sticky lg:top-24 rounded-2xl p-6 bg-gradient-to-b from-[#14141a] to-[#0d0d10] border border-[#d4af37]/30" data-testid="estimate-panel">
              <h2 className="font-display text-xl uppercase text-white mb-1">Your Selection</h2>
              <p className="text-[10px] text-zinc-500 font-mono mb-5">Priced services total instantly. Custom items are quoted after review.</p>

              {items.length === 0 ? (
                <p className="text-xs text-zinc-500 font-mono py-6 text-center" data-testid="estimate-empty">
                  No services selected yet.<br />Tap "Add" on any priced service.
                </p>
              ) : (
                <ul className="space-y-3 mb-5">
                  {items.map((i) => (
                    <li key={i.key} className="flex items-center justify-between gap-2 text-xs" data-testid={`estimate-item-${i.key}`}>
                      <div className="min-w-0">
                        <p className="text-zinc-200 truncate">{i.name}</p>
                        <p className="text-[10px] text-zinc-500 font-mono">
                          {i.unit_price != null ? `$${i.unit_price} × ${i.qty}` : "Custom quote after review"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {i.unit_price != null && (
                          <>
                            <button type="button" aria-label="decrease" onClick={() => setQty(i.key, -1)} data-testid={`estimate-qty-minus-${i.key}`} className="w-6 h-6 rounded border border-zinc-700 text-zinc-300 hover:border-[#d4af37] transition-colors">−</button>
                            <span className="w-5 text-center font-mono text-zinc-200">{i.qty}</span>
                            <button type="button" aria-label="increase" onClick={() => setQty(i.key, 1)} data-testid={`estimate-qty-plus-${i.key}`} className="w-6 h-6 rounded border border-zinc-700 text-zinc-300 hover:border-[#d4af37] transition-colors">+</button>
                          </>
                        )}
                        <button type="button" aria-label="remove" onClick={() => removeItem(i.key)} data-testid={`estimate-remove-${i.key}`} className="w-6 h-6 rounded text-zinc-500 hover:text-red-400 transition-colors">×</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="border-t border-[#d4af37]/20 pt-4 mb-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Estimated Total</span>
                  <span className="font-display text-3xl text-[#d4af37]" data-testid="estimate-total">
                    {estimate > 0 ? `$${estimate.toLocaleString()}` : "$0"}
                  </span>
                </div>
                {(hasQuoteOnly || items.some((i) => i.price_type === "starting" || i.price_type === "range")) && (
                  <p className="text-[10px] text-zinc-500 font-mono mt-2 leading-relaxed">
                    Includes minimums for starting/range prices. Custom-quote items are priced after DuroTech28 reviews your project.
                  </p>
                )}
              </div>

              {submitted || paid ? (
                <div className="text-center py-6" data-testid="estimate-success">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-[#d4af37]/50 flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#d4af37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                  {paid ? (
                    <>
                      <p className="text-sm text-white font-medium mb-1" data-testid="payment-success">Payment Confirmed</p>
                      <p className="text-[11px] text-zinc-400 font-mono">
                        ${paid.total.toLocaleString()} received via PayPal. DuroTech28 will confirm your appointment.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-white font-medium mb-1">Request Received</p>
                      <p className="text-[11px] text-zinc-400 font-mono">DuroTech28 will confirm your estimate and schedule.</p>
                    </>
                  )}
                  <button type="button" onClick={() => { setSubmitted(false); setPaid(null); setItems([]); }} data-testid="estimate-new-btn" className="mt-4 px-5 py-2 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors">
                    Start New Request
                  </button>
                </div>
              ) : (
                <>
                <form onSubmit={submit} className="space-y-3 text-xs font-mono" data-testid="estimate-form">
                  <input
                    type="text" required placeholder="Your Name" value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="estimate-name-input"
                    className="w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white focus:border-[#d4af37] outline-none transition-colors"
                  />
                  <input
                    type="text" required placeholder="Phone or Email" value={form.contact}
                    onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                    data-testid="estimate-contact-input"
                    className="w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white focus:border-[#d4af37] outline-none transition-colors"
                  />
                  <input
                    type="text" placeholder="Installation address / city (optional)" value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    data-testid="estimate-location-input"
                    className="w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white focus:border-[#d4af37] outline-none transition-colors"
                  />
                  <input
                    type="text" placeholder="Preferred date (optional)" value={form.preferred_date}
                    onChange={(e) => setForm((f) => ({ ...f, preferred_date: e.target.value }))}
                    data-testid="estimate-date-input"
                    className="w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white focus:border-[#d4af37] outline-none transition-colors"
                  />
                  <textarea
                    rows="2" placeholder="Project details — room, wall type, scope..." value={form.details}
                    onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
                    data-testid="estimate-details-input"
                    className="w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white focus:border-[#d4af37] outline-none transition-colors"
                  ></textarea>
                  <button
                    type="submit" disabled={submitting || items.length === 0}
                    data-testid="estimate-submit-btn"
                    className="w-full py-3.5 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25 disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : hasQuoteOnly && estimate === 0 ? "Request Custom Quote" : "Send Booking Request"}
                  </button>
                  <p className="text-center text-[10px] text-zinc-500">
                    Prefer to talk? <a href={PHONE_TEL} data-testid="estimate-call-link" className="text-[#d4af37] hover:underline">Call {PHONE_DISPLAY}</a>
                  </p>
                </form>
                {items.length > 0 && (
                  canCheckout ? (
                    <PayPalCheckout
                      items={items}
                      form={form}
                      onPaid={(result) => setPaid(result)}
                    />
                  ) : (
                    <p className="text-[10px] text-zinc-500 font-mono text-center leading-relaxed border border-zinc-800 rounded p-3 mt-3" data-testid="checkout-review-note">
                      Your list includes services that need a confirmed quote before payment — send the request and DuroTech28 will finalize pricing with you first.
                    </p>
                  )
                )}
                </>
              )}
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
